import React, { useMemo, useState, useRef, useEffect } from 'react'
import Icon from '../../components/Icon'
import { createPortal } from 'react-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../../components/DataTable'
import { useStudentsStore } from '../../../stores/studentsStore'
import { useGoalsStore } from '../../../stores/goalsStore'
import { useSchoolStore } from '../../../stores/schoolsStore'
import { useAuth } from '../../../contexts/AuthContext'
import {
  School,
  Grade,
  Race,
  Gender,
  Accommodation,
  GoalCategory,
  GoalArea,
} from '../../../types'
import { useNavigate } from 'react-router-dom'

interface FinalTabProps {
  importedData: any[]
  setActiveTab: (tab: string) => void
  setErrors: (errors: any[]) => void
  clearAllStudents: () => void
}

// Tooltip component that renders in a portal
const Tooltip: React.FC<{
  errors: string[]
  targetRef: React.RefObject<HTMLDivElement>
  visible: boolean
}> = ({ errors, targetRef, visible }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (visible && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollLeft =
        window.pageXOffset || document.documentElement.scrollLeft

      setPosition({
        top: rect.top + scrollTop - 8, // 8px gap above the element
        left: rect.left + scrollLeft + rect.width / 2, // Center horizontally
      })
    }
  }, [visible, targetRef])

  if (!visible) return null

  return createPortal(
    <div
      className="absolute z-[9999] bg-red-600 text-white text-xs rounded py-2 px-3 shadow-lg w-48 break-words pointer-events-none"
      style={{
        top: position.top,
        left: position.left,
        transform: 'translate(-50%, -100%)', // Center horizontally and position above
      }}
    >
      <div className="whitespace-normal">
        {errors.map((error, index) => (
          <div key={index} className="mb-1 last:mb-0">
            {error}
          </div>
        ))}
      </div>
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-4 border-transparent border-t-red-600"></div>
    </div>,
    document.body,
  )
}

// Category to areas mapping (from server Goal model)
const categoryAreaMap: Record<GoalCategory, GoalArea[]> = {
  [GoalCategory.EARLY_LEARNING_SKILLS]: [
    GoalArea.LANGUAGE_AND_LITERACY,
    GoalArea.EARLY_COGNITION_STEAM,
    GoalArea.SOCIAL_EMOTIONAL_DEVELOPMENT,
    GoalArea.PHYSICAL_MOTOR_DEVELOPMENT,
    GoalArea.APPROACHES_TO_LEARNING,
  ],
  [GoalCategory.ACADEMIC]: [
    GoalArea.READING_PHONEMIC_AWARENESS,
    GoalArea.READING_PHONICS,
    GoalArea.READING_FLUENCY,
    GoalArea.READING_VOCABULARY,
    GoalArea.READING_COMPREHENSION,
    GoalArea.MATH_CALCULATION,
    GoalArea.MATH_PROBLEM_SOLVING,
    GoalArea.WRITTEN_LANGUAGE_CONTENT,
    GoalArea.WRITTEN_LANGUAGE_MECHANICS,
    GoalArea.WRITTEN_LANGUAGE_EXPRESSION,
    GoalArea.SPEECH_LANGUAGE_ARTICULATION,
    GoalArea.SPEECH_LANGUAGE_VOICE,
    GoalArea.SPEECH_LANGUAGE_RECEPTIVE,
    GoalArea.SPEECH_LANGUAGE_EXPRESSIVE,
    GoalArea.SPEECH_LANGUAGE_PRAGMATICS,
    GoalArea.COMMUNICATION,
    GoalArea.COGNITIVE,
    GoalArea.PHYSICAL_EDUCATION,
    GoalArea.OTHER,
  ],
  [GoalCategory.SOCIAL_EMOTIONAL_BEHAVIORAL]: [
    GoalArea.SOCIAL_EMOTIONAL_BEHAVIORAL,
    GoalArea.SOCIAL_INTERACTION_SKILLS,
    GoalArea.SELF_MANAGEMENT,
    GoalArea.OTHER,
  ],
  [GoalCategory.HEALTH]: [
    GoalArea.HEALTH_MEDICAL,
    GoalArea.HEARING,
    GoalArea.VISION,
    GoalArea.ORIENTATION_MOBILITY,
    GoalArea.OTHER,
  ],
  [GoalCategory.PHYSICAL]: [
    GoalArea.FINE_MOTOR,
    GoalArea.VISUAL_MOTOR,
    GoalArea.HAND_SKILLS,
    GoalArea.FINE_MOTOR_COORDINATION,
    GoalArea.MANUAL_DEXTERITY,
    GoalArea.BIMANUAL_SKILLS,
    GoalArea.SENSORY_PROCESSING_STRATEGIES,
    GoalArea.GROSS_MOTOR,
    GoalArea.FUNCTIONAL_MOBILITY,
    GoalArea.POSTURAL_CONTROL,
    GoalArea.POSITIONING,
    GoalArea.COMMUNITY_ACCESS,
    GoalArea.INDEPENDENT_LIVING_FEEDING,
    GoalArea.INDEPENDENT_COMMUNITY_LIVING_REC,
    GoalArea.INDEPENDENT_COMMUNITY_LIVING_TOILETING,
    GoalArea.INDEPENDENT_COMMUNITY_LIVING_DRESSING,
    GoalArea.OTHER,
  ],
}

const FinalTab: React.FC<FinalTabProps> = ({
  importedData,
  setActiveTab,
  setErrors,
  clearAllStudents,
}) => {
  const { user } = useAuth()
  const { bulkImportStudents, isImporting: isImportingStudents } =
    useStudentsStore()
  const { bulkImportGoals, isImporting: isImportingGoals } = useGoalsStore()
  const navigate = useNavigate()

  const { schools } = useSchoolStore()
  const isImporting = isImportingStudents || isImportingGoals
  const [isProcessing, setIsProcessing] = useState(false)
  const [showOnlyErrors, setShowOnlyErrors] = useState(false)
  const [hoveredCell, setHoveredCell] = useState<{
    key: string
    index: number
    errors: string[]
  } | null>(null)

  // Helper function to find school ID by name or import aliases
  const findSchoolByName = (schoolName: string) => {
    const school = schools.find((s: School) => {
      // Check if it matches display name
      if (s.displayName.toLowerCase() === schoolName.toLowerCase()) {
        return true
      }
      // Check if it matches any import names
      if (s.importNames && s.importNames.length > 0) {
        return s.importNames.some(
          (importName) => importName.toLowerCase() === schoolName.toLowerCase(),
        )
      }
      return false
    })
    return school?._id
  }

  // Helper function to map grade format
  const mapGradeFormat = (grade: string) => {
    if (grade.startsWith('Grade ')) {
      return grade
    }
    if (grade === 'Pre-K' || grade === 'K') {
      return grade
    }
    const gradeNum = parseInt(grade)
    if (!isNaN(gradeNum) && gradeNum >= 1 && gradeNum <= 12) {
      return `Grade ${gradeNum}`
    }
    return grade
  }

  // Validation functions
  const validateField = (field: string, value: any, _student: any) => {
    const errors: string[] = []

    switch (field) {
      case 'firstName':
        if (!value || typeof value !== 'string' || !value.trim()) {
          errors.push('First name is required')
        }
        break

      case 'lastName':
        if (!value || typeof value !== 'string' || !value.trim()) {
          errors.push('Last name is required')
        }
        break

      case 'grade':
        if (!value || typeof value !== 'string' || !value.trim()) {
          errors.push('Grade is required')
        } else {
          const mappedGrade = mapGradeFormat(value)
          const validGrades = Object.values(Grade)
          if (!validGrades.includes(mappedGrade as Grade)) {
            errors.push(
              `Invalid grade. Must be one of: ${validGrades.join(', ')}`,
            )
          }
        }
        break

      case 'race':
        if (value && typeof value === 'string' && value.trim()) {
          const validRaces = Object.values(Race)
          if (!validRaces.includes(value as Race)) {
            errors.push(
              `Invalid race. Must be one of: ${validRaces.join(', ')}`,
            )
          }
        }
        break

      case 'gender':
        if (value && typeof value === 'string' && value.trim()) {
          const validGenders = Object.values(Gender)
          if (!validGenders.includes(value as Gender)) {
            errors.push(
              `Invalid gender. Must be one of: ${validGenders.join(', ')}`,
            )
          }
        }
        break

      case 'school':
        if (!value || typeof value !== 'string' || !value.trim()) {
          errors.push('School is required')
        } else {
          const schoolId = findSchoolByName(value)
          if (!schoolId) {
            errors.push(
              `School "${value}" not found or you don't have access to it`,
            )
          }
        }
        break

      case 'accommodation':
        if (value && typeof value === 'string' && value.trim()) {
          const validAccommodations = Object.values(Accommodation)
          if (!validAccommodations.includes(value as Accommodation)) {
            errors.push(`Invalid accommodation: ${value}`)
          }
        }
        break

      case 'accommodations':
        if (value && Array.isArray(value)) {
          const validAccommodations = Object.values(Accommodation)
          const invalidAccommodations = value.filter(
            (acc) => !validAccommodations.includes(acc),
          )
          if (invalidAccommodations.length > 0) {
            errors.push(
              `Invalid accommodations: ${invalidAccommodations.join(', ')}`,
            )
          }
        }
        break

      case 'description':
        if (!value || typeof value !== 'string' || !value.trim()) {
          errors.push('Goal description is required')
        }
        break

      case 'category':
        if (!value || typeof value !== 'string' || !value.trim()) {
          errors.push('Goal category is required')
        } else {
          const validCategories = Object.values(GoalCategory)
          if (!validCategories.includes(value as GoalCategory)) {
            errors.push(
              `Invalid category. Must be one of: ${validCategories.join(', ')}`,
            )
          }
        }
        break

      case 'area':
        if (!value || typeof value !== 'string' || !value.trim()) {
          errors.push('Goal area is required')
        } else {
          const validAreas = Object.values(GoalArea)
          if (!validAreas.includes(value as GoalArea)) {
            errors.push(`Invalid area: ${value}`)
          }
          // Also validate that the area is valid for the selected category
          if (_student.category) {
            const allowedAreas =
              categoryAreaMap[_student.category as GoalCategory]
            if (allowedAreas && !allowedAreas.includes(value as GoalArea)) {
              errors.push(
                `Area "${value}" is not valid for category "${_student.category}"`,
              )
            }
          }
        }
        break

      case 'objectives':
        if (!value || typeof value !== 'string' || !value.trim()) {
          errors.push('Objectives are required')
        }
        break

      default:
        break
    }

    return errors
  }

  // Check if a field has validation errors
  const hasFieldError = (field: string, value: any, student: any) => {
    return validateField(field, value, student).length > 0
  }

  // Get validation error messages for a field
  const getFieldErrors = (field: string, value: any, student: any) => {
    return validateField(field, value, student)
  }

  // Check if a row has any validation errors
  const hasRowErrors = (student: any) => {
    const keys = Object.keys(student).filter(
      (key) =>
        key !== 'importedAt' &&
        key !== '_originalRowNumber' &&
        key !== '_importType',
    )
    return keys.some((key) => hasFieldError(key, student[key], student))
  }

  // Filter data based on the toggle
  const displayData = useMemo(() => {
    if (!showOnlyErrors) {
      return importedData
    }

    return importedData.filter((student) => hasRowErrors(student))
  }, [importedData, showOnlyErrors, schools])

  // Generate columns dynamically with validation styling
  const columns = useMemo<ColumnDef<any>[]>(() => {
    if (displayData.length === 0) return []

    const keys = Object.keys(displayData[0]).filter(
      (key) =>
        key !== 'importedAt' &&
        key !== '_originalRowNumber' &&
        key !== '_importType',
    )

    // Create CSV Row # column first
    const csvRowColumn: ColumnDef<any> = {
      accessorKey: '_originalRowNumber',
      header: 'CSV Row #',
      cell: (info: any) => {
        const value = info.getValue()
        return (
          <div className="text-sm font-medium text-gray-900 bg-gray-50 px-2 py-1 rounded">
            {value || 'N/A'}
          </div>
        )
      },
      size: 100,
    }

    const baseColumns = keys.map((key) => ({
      accessorKey: key,
      header: key.charAt(0).toUpperCase() + key.slice(1),
      cell: (info: any) => {
        const value = info.getValue()
        const student = info.row.original
        const hasError = hasFieldError(key, value, student)
        const errors = hasError ? getFieldErrors(key, value, student) : []
        const cellRef = useRef<HTMLDivElement>(null)
        const rowIndex = info.row.index

        return (
          <div
            ref={cellRef}
            className="relative"
            onMouseEnter={() => {
              if (hasError && errors.length > 0) {
                setHoveredCell({ key, index: rowIndex, errors })
              }
            }}
            onMouseLeave={() => {
              setHoveredCell(null)
            }}
          >
            <div
              className={`text-sm px-2 py-1 rounded ${
                hasError
                  ? 'text-red-900 bg-red-50 border border-red-200'
                  : 'text-gray-900'
              }`}
            >
              {String(value || '')}
            </div>
            {hoveredCell?.key === key && hoveredCell?.index === rowIndex && (
              <Tooltip errors={errors} targetRef={cellRef} visible={true} />
            )}
          </div>
        )
      },
    }))

    // Start with CSV Row # column, then add the rest
    const allColumns = [csvRowColumn, ...baseColumns]

    // Add errors column when showing only errors
    if (showOnlyErrors) {
      allColumns.push({
        accessorKey: 'validationErrors',
        header: 'Validation Errors',
        cell: (info: any) => {
          const student = info.row.original
          const keys = Object.keys(student).filter(
            (key) => key !== 'importedAt' && key !== '_originalRowNumber',
          )
          const allErrors: string[] = []

          keys.forEach((key) => {
            const fieldErrors = getFieldErrors(key, student[key], student)
            fieldErrors.forEach((error) => {
              allErrors.push(`${key}: ${error}`)
            })
          })

          return (
            <div className="text-sm text-red-600">
              {allErrors.length > 0 && (
                <div className="space-y-1">
                  {allErrors.map((error, errorIndex) => (
                    <div key={errorIndex} className="text-xs">
                      • {error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        },
      })
    }

    return allColumns
  }, [displayData, schools, hoveredCell, showOnlyErrors])

  // Count total validation errors across all students
  const getTotalValidationErrors = () => {
    let totalErrors = 0
    importedData.forEach((student) => {
      Object.keys(student).forEach((key) => {
        if (key !== 'importedAt' && key !== '_originalRowNumber') {
          const errors = validateField(key, student[key], student)
          totalErrors += errors.length
        }
      })
    })
    return totalErrors
  }

  const totalValidationErrors = getTotalValidationErrors()

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Final Review</h3>
        {importedData.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-gray-600">
                Review{' '}
                {showOnlyErrors ? displayData.length : importedData.length}
                {showOnlyErrors ? ' rows with errors' : ' records'} before
                completing the import.
                {showOnlyErrors &&
                  importedData.length !== displayData.length &&
                  ` (${importedData.length} total records)`}
              </p>

              {/* Toggle Switch for "Only Rows with Errors" */}
              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <span className="text-sm font-medium text-gray-700 mr-3">
                    Only Rows with Errors
                  </span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={showOnlyErrors}
                      onChange={(e) => setShowOnlyErrors(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`block w-14 h-8 rounded-full transition-colors ${
                        showOnlyErrors ? 'bg-red-600' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${
                          showOnlyErrors ? 'transform translate-x-6' : ''
                        }`}
                      />
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {totalValidationErrors > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Icon type="x-circle" className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Validation Errors Found
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        {totalValidationErrors} validation error
                        {totalValidationErrors !== 1 ? 's' : ''} found in your
                        data. Fields with errors are highlighted in red. Hover
                        over them to see error details.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showOnlyErrors &&
              displayData.length === 0 &&
              totalValidationErrors === 0 && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Icon type="check-circle" className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        No Validation Errors Found
                      </h3>
                      <div className="mt-2 text-sm text-green-700">
                        <p>
                          All data is valid and ready for import! No rows have
                          validation errors.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      <DataTable
        data={displayData}
        columns={columns}
        searchPlaceholder="Search imported data..."
        emptyStateTitle="No data imported"
        emptyStateDescription="No data imported yet. Please go to the Import tab to upload and process your data."
        emptyStateIcon={
          <Icon type="document-text" className="mx-auto h-12 w-12 text-gray-400" />
        }
        showCreateButton={false}
      />

      {importedData.length > 0 && (
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setActiveTab('import')}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Back to Import
          </button>
          <button
            onClick={async () => {
              // Prevent import if there are validation errors
              if (totalValidationErrors > 0) {
                alert(
                  `Cannot import data with ${totalValidationErrors} validation error${totalValidationErrors !== 1 ? 's' : ''}. Please fix the highlighted errors first.`,
                )
                return
              }

              setIsProcessing(true)

              try {
                // Process imported data to combine duplicates and merge accommodations
                const dataMap = new Map()

                importedData.forEach((item) => {
                  const {
                    studentId,
                    accommodation,
                    _originalRowNumber,
                    ...rest
                  } = item

                  if (dataMap.has(studentId)) {
                    // If ID already exists, add the accommodation to the accommodations array
                    const existing = dataMap.get(studentId)
                    if (
                      accommodation &&
                      !existing.accommodations.includes(accommodation)
                    ) {
                      existing.accommodations.push(accommodation)
                    }
                    // Keep the original row number from the first occurrence
                  } else {
                    // Create new entry with accommodations array and preserve original row number
                    dataMap.set(studentId, {
                      ...rest,
                      studentId,
                      accommodations: accommodation ? [accommodation] : [],
                      _originalRowNumber: _originalRowNumber, // Preserve the first occurrence row number
                    })
                  }
                })

                const filteredData = Array.from(dataMap.values())

                // Detect import type from first record
                const isGoalsImport =
                  importedData.length > 0 &&
                  importedData[0]._importType === 'goals'

                // Transform data to match Student schema and validate
                const organizationId =
                  typeof user?.organization === 'string'
                    ? user.organization
                    : user?.organization._id

                const validationErrors: any[] = []
                const validStudents: any[] = []

                filteredData.forEach((item, index) => {
                  const errors: string[] = []

                  if (isGoalsImport) {
                    // Goals import validation
                    if (!item.studentId?.trim()) {
                      errors.push('Student ID is required')
                    }
                    if (!item.firstName?.trim()) {
                      errors.push('First name is required')
                    }
                    if (!item.lastName?.trim()) {
                      errors.push('Last name is required')
                    }
                    if (!item.category?.trim()) {
                      errors.push('Category is required')
                    }
                    if (!item.area?.trim()) {
                      errors.push('Area is required')
                    }
                    if (!item.description?.trim()) {
                      errors.push('Description is required')
                    }
                    if (!item.objectives?.trim()) {
                      errors.push('Objectives are required')
                    }

                    // Validate organization
                    if (!organizationId) {
                      errors.push('Organization not found')
                    }

                    if (errors.length > 0) {
                      validationErrors.push({
                        index,
                        error: errors.join('; '),
                        student: item,
                      })
                    } else {
                      validStudents.push({
                        studentId: item.studentId.trim(),
                        firstName: item.firstName.trim(),
                        lastName: item.lastName.trim(),
                        category: item.category,
                        area: item.area,
                        description: item.description.trim(),
                        objectives: item.objectives.trim(),
                        organization: organizationId,
                      })
                    }
                  } else {
                    // Student/Accommodation import validation
                    if (!item.firstName?.trim()) {
                      errors.push('First name is required')
                    }
                    if (!item.lastName?.trim()) {
                      errors.push('Last name is required')
                    }
                    if (!item.grade?.trim()) {
                      errors.push('Grade is required')
                    }

                    // Validate school
                    const schoolId = findSchoolByName(item.school)
                    if (!schoolId) {
                      errors.push(
                        `School "${item.school}" not found or you don't have access to it`,
                      )
                    }

                    // Validate organization
                    if (!organizationId) {
                      errors.push('Organization not found')
                    }

                    if (errors.length > 0) {
                      validationErrors.push({
                        index,
                        error: errors.join('; '),
                        student: item,
                      })
                    } else {
                      validStudents.push({
                        studentId: item.studentId.trim(),
                        firstName: item.firstName.trim(),
                        lastName: item.lastName.trim(),
                        grade: mapGradeFormat(item.grade),
                        race: item.race || undefined,
                        gender: item.gender || undefined,
                        caseManager: item.caseManager || undefined,
                        accommodations: item.accommodations || [],
                        school: schoolId,
                        organization: organizationId,
                      })
                    }
                  }
                })

                // If there are validation errors, show them and stop
                if (validationErrors.length > 0) {
                  setErrors(validationErrors)
                  setActiveTab('errors')
                  alert(
                    `${validationErrors.length} students have validation errors. Please check the Errors tab.`,
                  )
                  return
                }

                if (validStudents.length === 0) {
                  alert('No valid students to import.')
                  return
                }

                let result: any

                // Route to the correct import endpoint based on import type
                if (isGoalsImport) {
                  // Goals import
                  const goalsData = validStudents.map((item) => ({
                    studentId: item.studentId.trim(),
                    firstName: item.firstName.trim(),
                    lastName: item.lastName.trim(),
                    category: item.category,
                    area: item.area,
                    description: item.description.trim(),
                    objectives: item.objectives.trim(),
                  }))

                  result = await bulkImportGoals(goalsData)
                } else {
                  // Student/Accommodation import
                  result = await bulkImportStudents(validStudents)
                }

                let message = ''
                if (isGoalsImport) {
                  message = `Import completed!\n${result.created} goals imported successfully`
                  if (result.studentsCreated > 0) {
                    message += `\n${result.studentsCreated} new students created`
                  }
                  if (result.errors && result.errors.length > 0) {
                    message += `\n${result.errors.length} goals failed to import`
                    // Set errors for the Errors tab
                    setErrors(result.errors)
                    // Switch to errors tab to show detailed error information
                    setActiveTab('errors')
                  } else {
                    // Clear any previous errors on successful import
                    setErrors([])
                    clearAllStudents()
                  }
                } else {
                  message = `Import completed!\n${result.successful} students imported successfully`
                  if (result.duplicates > 0) {
                    message += `\n${result.duplicates} duplicate students ignored`
                  }
                  if (result.failed > 0) {
                    message += `\n${result.failed} students failed to import`
                    // Set errors for the Errors tab
                    setErrors(result.errors)
                    // Switch to errors tab to show detailed error information
                    setActiveTab('errors')
                  } else {
                    // Clear any previous errors on successful import
                    setErrors([])
                    clearAllStudents()
                  }
                }

                alert(message)

                // If all successful, go back to main students view
                const hasErrors = isGoalsImport
                  ? result.errors && result.errors.length > 0
                  : result.failed > 0
                if (!hasErrors) {
                  clearAllStudents()
                  navigate('/manage/students')
                }
              } catch (error: any) {
                console.error('Import failed:', error)

                // Create a general error for the Errors tab
                const generalError = {
                  index: 0,
                  error:
                    error.response?.data?.data.errors[0].error ||
                    error.message ||
                    'Unknown import error occurred',
                  student: error.response?.data?.data.errors[0].student || null,
                }

                setErrors([generalError])
                setActiveTab('errors')

                alert(
                  `Import failed: ${error.response?.data?.error || error.message}. Check the Errors tab for details.`,
                )
              } finally {
                setIsProcessing(false)
              }
            }}
            disabled={
              isProcessing ||
              isImporting ||
              importedData.length === 0 ||
              totalValidationErrors > 0
            }
            className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
              totalValidationErrors > 0
                ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
                : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
            }`}
          >
            {(isProcessing || isImporting) && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {totalValidationErrors > 0
              ? 'Fix Errors to Import'
              : 'Complete Import'}
          </button>
        </div>
      )}
    </div>
  )
}

export default FinalTab
